/**
 * Fluxible actions for use in the invite flow.
 */
import type Q from 'q';

import type { ActionContext } from 'js/lib/ActionContext';

import _t from 'i18n!nls/staff-management';

import type { NotificationParam } from '../../common/notifications/Notifications';
import { staffMemberships } from '../apis';
import { EVENT_NAMES } from '../constants';
import type { StaffMembershipType } from '../types';

/**
 * Present the user with an option to invite users to coursera.
 *
 * Call without any props to hide the modal.
 *
 * This will trigger a modal in the flow, forcing user to confirm or abort the invitation.
 */
export const prompt = (
  /** Provided by fluxible. */
  actionContext: ActionContext,
  /** Users opted in for invitation by email for particular roles for a particular entity of a particular type. */
  {
    fromId,
    roles = [],
    type,
    users = [],
  }: {
    fromId?: string;
    roles?: Array<string>;
    type?: keyof typeof StaffMembershipType;
    users?: Array<string>;
  } = {}
): void => {
  actionContext.dispatch(EVENT_NAMES.UI, { inviteFlow: { fromId, roles, type, users } });
};

/**
 * Invite people to join coursera so we can add eventually add them as members.
 */
export const confirm = (
  /** Provided by fluxible. */
  actionContext: ActionContext,
  /** List of user emails to invite to coursera for a particular type of role for a particular partner. */
  {
    fromId,
    roles,
    type,
    users,
    // API DI.
    _invite = staffMemberships.invite,
    notify,
  }: {
    fromId?: string;
    roles?: Array<string>;
    type?: keyof typeof StaffMembershipType;
    users?: Array<string>;
    _invite?(args: {
      fromId: string | number;
      roleId: string;
      type: keyof typeof StaffMembershipType;
      users: Array<string>;
    }): Q.Promise<void>;
    notify: (notification: NotificationParam) => void;
  }
) => {
  const inviteFlowState = actionContext.getStore('StaffManagementStoreV2').ui('inviteFlow');
  const resolvedFromId = fromId || inviteFlowState.fromId;
  const resolvedRoles = roles || inviteFlowState.roles;
  const resolvedType = type || inviteFlowState.type;
  const resolvedUsers = users || inviteFlowState.users;

  // Close the modal if it's still open.
  actionContext.executeAction(prompt);
  return (
    // Due to the way the batch inviting works, we only invite to one role.
    _invite({
      fromId: resolvedFromId,
      roleId: resolvedRoles[0],
      type: resolvedType,
      users: resolvedUsers,
    })
      .then(() => {
        notify({ message: _t('Invitations sent.'), type: 'success' });
      })
      .catch(() => {
        notify({
          message: _t('Error while inviting users. Some or all of the invitations may not have been sent.'),
          type: 'error',
        });
      })
  );
};
